import { animate, motion, useMotionValue } from "motion/react"
import { useEffect, useState } from "react"
import useMeasure from "react-use-measure"

import { cn } from "@/lib/utils"

export type InfiniteSliderProps = {
  children: React.ReactNode
  gap?: number
  speed?: number
  speedOnHover?: number
  reverse?: boolean
  className?: string
}

export const InfiniteSlider = ({
  children,
  gap = 16,
  speed = 40,
  speedOnHover,
  reverse = false,
  className
}: InfiniteSliderProps) => {
  const [currentSpeed, setCurrentSpeed] = useState(speed)
  const [ref, { width }] = useMeasure()
  const translation = useMotionValue(0)
  const [isTransitioning, setIsTransitioning] = useState(false)
  const [key, setKey] = useState(0)

  useEffect(() => {
    let controls: { stop: () => void } | undefined
    if (width === 0) return () => undefined

    const contentSize = width + gap
    const from = reverse ? -contentSize / 2 : 0
    const to = reverse ? 0 : -contentSize / 2
    const distanceToTravel = Math.abs(to - from)
    const duration = distanceToTravel / currentSpeed

    if (isTransitioning) {
      const remainingDistance = Math.abs(translation.get() - to)
      const transitionDuration = remainingDistance / currentSpeed
      controls = animate(translation, [translation.get(), to], {
        ease: "linear",
        duration: transitionDuration,
        onComplete: () => {
          setIsTransitioning(false)
          setKey((k) => k + 1)
        }
      })
    } else {
      controls = animate(translation, [from, to], {
        ease: "linear",
        duration,
        repeat: Infinity,
        repeatType: "loop",
        repeatDelay: 0,
        onRepeat: () => {
          translation.set(from)
        }
      })
    }

    return () => controls?.stop()
  }, [key, translation, currentSpeed, width, gap, isTransitioning, reverse])

  const hoverProps =
    speedOnHover !== undefined
      ? {
          onHoverStart: () => {
            setIsTransitioning(true)
            setCurrentSpeed(speedOnHover)
          },
          onHoverEnd: () => {
            setIsTransitioning(true)
            setCurrentSpeed(speed)
          }
        }
      : {}

  return (
    <div className={cn("overflow-hidden", className)}>
      <motion.div
        ref={ref}
        className="flex w-max"
        style={{
          x: translation,
          gap: `${gap}px`
        }}
        {...hoverProps}
      >
        {children}
        {children}
      </motion.div>
    </div>
  )
}
